import {
  of,
  map,
  catchError,
  switchMap,
  from,
  EMPTY,
  Observable,
  concatMap,
  filter,
  withLatestFrom,
} from 'rxjs';
import toast from 'react-hot-toast';
import { ofType } from 'redux-observable';
import {
  BATCH_CODES_UPDATE,
  UPDATE_CODE,
  ADD_CODE_TO_NOTE,
  client,
  ADD_FORM_SELECTION,
  UPDATE_NOTE_PART,
} from '@ax/graphql';
import { AnalyticsService, getProviderSettings } from '@ax/service';
import mixpanel from 'mixpanel-browser';
import {
  DEVICE_ID_STORAGE_KEY,
  ANALYTICS_CONSTANTS,
  APP_NAME,
  NO_ICD_10_CODE,
  CODE_SYSTEM_ICD_10_INTERNAL,
} from '@ax/constants';
import { UAParser } from 'ua-parser-js';
import { Auth } from '@ax/auth';
import { PayloadAction } from '@reduxjs/toolkit';
import { getRandomString } from './utilities.ts';
import { ADD_FREE_TEXT } from '@ax/graphql';
import { Sentence } from '@ax/constants';
import { selectProviderUserInfo } from '../provider/selectors.ts';
import { selectMixpanelToken } from '../mixpanel/selectors.ts';
import { mixpanelSlice } from '../mixpanel';
import { RootState } from '../../';
import { noteSlice } from './';

export const onICD10CODESelected = (action$: Observable<any>) => {
  return action$.pipe(
    ofType(noteSlice.actions.icd10CodeSelected.type),
    switchMap((action: PayloadAction<any>) => {
      if (
        !action.payload.currentSelection &&
        !action.payload.newCode.id &&
        action.payload.newCode.code === NO_ICD_10_CODE
      ) {
        const mutation = {
          mutation: ADD_CODE_TO_NOTE,
          variables: {
            noteId: action.payload.noteId.toString(),
            input: {
              code: NO_ICD_10_CODE,
              codeSystem: CODE_SYSTEM_ICD_10_INTERNAL,
              display: 'No ICD-10 Code',
              actionSource: 'User',
              referenceSelection: action.payload.newCode.referenceSelection,
              referenceType: 'COMPLAINT',
              status: 'ACCEPTED',
              rank: action.payload.newCode.rank,
            },
          },
        };

        return from(client.mutate(mutation)).pipe(
          map((_) =>
            noteSlice.actions.noICD10CodeCreatedSucceeded({
              newCode: action.payload.newCode,
              currentSelection: action.payload.currentSelection,
              templateName: action.payload.templateName,
              section: action.payload.section,
            }),
          ),
          catchError((error) => {
            return of(noteSlice.actions.noICD10CodeCreatedFailed(error));
          }),
        );
      }

      if (
        action.payload.currentSelection &&
        !action.payload.newCode.id &&
        action.payload.newCode.code === NO_ICD_10_CODE
      ) {
        const mutation = {
          mutation: ADD_CODE_TO_NOTE,
          variables: {
            noteId: action.payload.noteId.toString(),
            input: {
              code: NO_ICD_10_CODE,
              codeSystem: CODE_SYSTEM_ICD_10_INTERNAL,
              display: 'No ICD-10 Code',
              actionSource: 'User',
              referenceSelection: action.payload.currentSelection.referenceSelection,
              referenceType: 'COMPLAINT',
              status: 'ACCEPTED',
              rank: action.payload.newCode.rank,
            },
          },
        };

        return from(client.mutate(mutation)).pipe(
          switchMap((_) => {
            const mutation = {
              mutation: UPDATE_CODE,
              variables: {
                updateCodeId: action.payload.currentSelection.id,
                input: {
                  status: 'REJECTED',
                  actionSource: 'User',
                },
              },
            };

            return from(client.mutate(mutation)).pipe(
              map((_) =>
                noteSlice.actions.icd10CodeReplaceSucceeded({
                  newCode: action.payload.newCode,
                  currentSelection: action.payload.currentSelection,
                  templateName: action.payload.templateName,
                  section: action.payload.section,
                }),
              ),
              catchError((error) => {
                return of(noteSlice.actions.icd10CodeReplaceFailed(error));
              }),
            );
          }),
          catchError((error) => {
            return of(noteSlice.actions.noICD10CodeCreatedFailed(error));
          }),
        );
      }

      if (!action.payload.currentSelection && action.payload.newCode) {
        const mutation = {
          mutation: UPDATE_CODE,
          variables: {
            updateCodeId: action.payload.newCode.id,
            input: {
              status: 'ACCEPTED',
              actionSource: 'User',
            },
          },
        };

        return from(client.mutate(mutation)).pipe(
          map((_) =>
            noteSlice.actions.icd10CodeAcceptSucceeded({
              newCode: action.payload.newCode,
              currentSelection: action.payload.currentSelection,
              templateName: action.payload.templateName,
              section: action.payload.section,
            }),
          ),
          catchError((error) => of(noteSlice.actions.icd10CodeAcceptFailed(error))),
        );
      }

      if (action.payload.currentSelection && action.payload.newCode) {
        const mutation = {
          mutation: BATCH_CODES_UPDATE,
          variables: {
            rejectCodeInput: {
              actionSource: 'User',
              status: 'REJECTED',
            },
            rejectCodeId: action.payload.currentSelection.id,
            acceptCodeInput: {
              actionSource: 'User',
              status: 'ACCEPTED',
            },
            acceptCodeId: action.payload.newCode.id,
          },
        };

        return from(client.mutate(mutation)).pipe(
          map((_) =>
            noteSlice.actions.icd10CodeReplaceSucceeded({
              newCode: action.payload.newCode,
              currentSelection: action.payload.currentSelection,
              templateName: action.payload.templateName,
              section: action.payload.section,
            }),
          ),
          catchError((error) => of(noteSlice.actions.icd10CodeReplaceFailed(error))),
        );
      }

      return EMPTY;
    }),
  );
};

export const createReEvaluation = (action$: Observable<any>) => {
  return action$.pipe(
    ofType(noteSlice.actions.createReevaluationRequested.type),
    switchMap((action: PayloadAction<{ noteId: string; content: string; order: number }>) => {
      const mutation = {
        mutation: ADD_FREE_TEXT,
        variables: {
          noteId: action.payload.noteId,
          SharedId: action.payload.noteId,
          Section: 'PROGRESS',
          ActionSource: 'User',
          Order: action.payload.order,
          Content: action.payload.content,
        },
      };

      return from(client.mutate(mutation)).pipe(
        concatMap(() => {
          return [
            noteSlice.actions.createReevaluationSucceeded({
              noteId: action.payload.noteId,
              content: action.payload.content,
              order: action.payload.order,
            }),
            noteSlice.actions.addReevaluationEnded({
              noteId: action.payload.noteId,
              status: 'Succeeded',
              withError: false,
            }),
          ];
        }),
        catchError((error) => {
          toast.error('An error occurred while creating the Re-Eval entry. Please, try again.');
          return of(
            noteSlice.actions.createReevaluationFailed({
              noteId: action.payload.noteId,
              content: action.payload.content,
              order: action.payload.order,
              errorMessage: error.message ?? 'An unknown error occurred while creating the Re-Eval entry.',
            }),
          );
        }),
      );
    }),
  );
};

export const onReevalSentenceUpdated = (action$: Observable<any>) => {
  return action$.pipe(
    ofType(noteSlice.actions.reevalSentenceUpdateRequested.type),
    concatMap(
      (
        action: PayloadAction<{
          selection: string;
          noteId: string;
          content: string;
          sentence: Sentence;
        }>,
      ) => {
        const mutation = {
          mutation: UPDATE_NOTE_PART,
          variables: {
            noteId: action.payload.noteId,
            SharedId: action.payload.noteId,
            selection: action.payload.sentence.selection,
            Content: action.payload.content,
            ActionSource: 'User',
            UpdatedAt: new Date().toISOString(),
          },
        };

        return from(client.mutate(mutation)).pipe(
          map(() => noteSlice.actions.reevalSentenceUpdateSucceeded(action.payload)),
          catchError(() => of(noteSlice.actions.reevalSentenceUpdateFailed(action.payload))),
        );
      },
    ),
  );
};

export const onReEvalSentenceModalOpenedDispatchMixpanelAnalytics = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(noteSlice.actions.addReevaluationStarted.type),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const { noteId } = action.payload;
        const providerUserInfo = selectProviderUserInfo(state);
        const mixpanelToken = selectMixpanelToken(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const event = analyticsService.onAddReEvaluationModalOpened({ noteId });

        return [mixpanelSlice.actions.eventDispatchSucceeded({ ...event })];
      } catch (error: unknown) {
        return [
          mixpanelSlice.actions.eventDispatchFailed({
            event: ANALYTICS_CONSTANTS.event.addReEvaluationModalOpened,
            error: (error as Error).message,
          }),
        ];
      }
    }),
  );

export const onReEvalSentenceModalClosedDispatchMixpanelAnalytics = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(noteSlice.actions.addReevaluationEnded.type),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const { noteId } = action.payload;
        const providerUserInfo = selectProviderUserInfo(state);
        const mixpanelToken = selectMixpanelToken(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const event = analyticsService.onAddReEvaluationModalClosed({ noteId });

        return [mixpanelSlice.actions.eventDispatchSucceeded({ ...event })];
      } catch (error: unknown) {
        return [
          mixpanelSlice.actions.eventDispatchFailed({
            event: ANALYTICS_CONSTANTS.event.addReEvaluationModalClosed,
            error: (error as Error).message,
          }),
        ];
      }
    }),
  );

export const onICD10CODESelectedDispatchMixpanelAnalytics = (action$: Observable<any>) =>
  action$.pipe(
    ofType(
      noteSlice.actions.icd10CodeAcceptSucceeded.type,
      noteSlice.actions.icd10CodeReplaceSucceeded.type,
      noteSlice.actions.noICD10CodeCreatedSucceeded.type,
    ),
    concatMap((action) => {
      return from(getProviderSettings()).pipe(
        switchMap((response) => {
          const token = response.mixpanelSettings?.find((item: any) => item.app === 'go')?.token;

          if (!token) {
            return EMPTY;
          }

          mixpanel.init(token, {
            debug: process.env.NODE_ENV === 'development',
            cross_subdomain_cookie: false,
          });
          const deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);

          const parser = new UAParser();
          const browser = parser.getBrowser();
          const os = parser.getOS();

          let event = ANALYTICS_CONSTANTS.event.icd10CodeUpdated;
          if (!action.payload.currentSelection && action.payload.newCode) {
            event = ANALYTICS_CONSTANTS.event.icd10CodeSelected;
          }

          const properties = {
            [ANALYTICS_CONSTANTS.properties.noteId]: action.payload.noteId,
            [ANALYTICS_CONSTANTS.properties.noteSection]: action.payload.section,
            [ANALYTICS_CONSTANTS.properties.templateName]: action.payload.templateName,
            [ANALYTICS_CONSTANTS.properties.icd10Code]: action.payload.newCode.code,
            [ANALYTICS_CONSTANTS.properties.providerGuid]: Auth.currentUser()?.guid || '',
            [ANALYTICS_CONSTANTS.properties.providerUid]: Auth.currentUser()?.uid || '',
            [ANALYTICS_CONSTANTS.properties.providerName]: response.name,
            [ANALYTICS_CONSTANTS.properties.providerEmail]: response.email,
            [ANALYTICS_CONSTANTS.properties.browserName]: browser.name ?? 'Unknown',
            [ANALYTICS_CONSTANTS.properties.browserVersion]: browser.version,
            [ANALYTICS_CONSTANTS.properties.os]: os.name ?? 'Unknown',
            [ANALYTICS_CONSTANTS.properties.osVersion]: os.version,
            [ANALYTICS_CONSTANTS.properties.appName]: APP_NAME,
            [ANALYTICS_CONSTANTS.properties.enterprise]: response.siteName,
            [ANALYTICS_CONSTANTS.properties.ehr]: response.ehrName,
            [ANALYTICS_CONSTANTS.properties.specialty]: response.specialtyName,
            [ANALYTICS_CONSTANTS.properties.deviceId]: deviceId ?? '',
          };

          mixpanel.track(event, properties);
          return [mixpanelSlice.actions.eventDispatchSucceeded({ event, properties })];
        }),
        catchError((error) => {
          return [
            mixpanelSlice.actions.eventDispatchFailed({
              error: error,
              event: ANALYTICS_CONSTANTS.event.careCuesSeen,
            }),
          ];
        }),
      );
    }),
  );

export const onICD10DropdownOpenedDispatchMixpanelAnalytics = (action$: Observable<any>) =>
  action$.pipe(
    ofType(noteSlice.actions.icd10DropdownOpened.type),
    concatMap((action: PayloadAction<any>) => {
      return from(getProviderSettings()).pipe(
        switchMap((response) => {
          const token = response.mixpanelSettings?.find((item: any) => item.app === 'go')?.token;

          if (!token) {
            return EMPTY;
          }

          mixpanel.init(token, {
            debug: process.env.NODE_ENV === 'development',
            cross_subdomain_cookie: false,
          });
          const deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);

          const parser = new UAParser();
          const browser = parser.getBrowser();
          const os = parser.getOS();

          const event = ANALYTICS_CONSTANTS.event.icd10DropdownExpanded;

          const properties = {
            [ANALYTICS_CONSTANTS.properties.noteId]: action.payload.noteId,
            [ANALYTICS_CONSTANTS.properties.noteSection]: action.payload.section,
            [ANALYTICS_CONSTANTS.properties.templateName]: action.payload.templateName,
            [ANALYTICS_CONSTANTS.properties.providerGuid]: Auth.currentUser()?.guid || '',
            [ANALYTICS_CONSTANTS.properties.providerUid]: Auth.currentUser()?.uid || '',
            [ANALYTICS_CONSTANTS.properties.providerName]: response.name,
            [ANALYTICS_CONSTANTS.properties.providerEmail]: response.email,
            [ANALYTICS_CONSTANTS.properties.browserName]: browser.name ?? 'Unknown',
            [ANALYTICS_CONSTANTS.properties.browserVersion]: browser.version,
            [ANALYTICS_CONSTANTS.properties.os]: os.name ?? 'Unknown',
            [ANALYTICS_CONSTANTS.properties.osVersion]: os.version,
            [ANALYTICS_CONSTANTS.properties.appName]: APP_NAME,
            [ANALYTICS_CONSTANTS.properties.enterprise]: response.siteName,
            [ANALYTICS_CONSTANTS.properties.ehr]: response.ehrName,
            [ANALYTICS_CONSTANTS.properties.specialty]: response.specialtyName,
            [ANALYTICS_CONSTANTS.properties.deviceId]: deviceId ?? '',
          };

          mixpanel.track(event, properties);
          return [mixpanelSlice.actions.eventDispatchSucceeded({ event, properties })];
        }),
        catchError((error) => {
          return [
            mixpanelSlice.actions.eventDispatchFailed({
              error: error,
              event: ANALYTICS_CONSTANTS.event.careCuesSeen,
            }),
          ];
        }),
      );
    }),
  );

export const addOrUpdateFormValue = (action$: Observable<any>) => {
  return action$.pipe(
    ofType(noteSlice.actions.noteHSFormValueUpdateRequested.type),
    filter((action) => action.payload.value !== undefined),
    concatMap((action) => {
      const { noteId, identifier, valueKey, value } = action.payload;
      const mutation = {
        mutation: ADD_FORM_SELECTION,
        variables: {
          input: {
            noteId,
            IdentityLabel: identifier,
            Section: 'ORGANIZE',
            Data: value,
            DataType: 'TEXT',
            SharedId: getRandomString(40),
            ActionSource: 'User',
          },
        },
      };

      return from(client.mutate(mutation)).pipe(
        map(() => {
          return noteSlice.actions.noteHSFormValueUpdated({
            noteId,
            identifier,
            valueKey,
            value,
          });
        }),
        catchError(() => {
          return of(noteSlice.actions.noteHSFormValueUpdateFailed(action.payload));
        }),
      );
    }),
  );
};

export default [
  onICD10CODESelected,
  onICD10CODESelectedDispatchMixpanelAnalytics,
  onICD10DropdownOpenedDispatchMixpanelAnalytics,
  onReEvalSentenceModalOpenedDispatchMixpanelAnalytics,
  onReEvalSentenceModalClosedDispatchMixpanelAnalytics,
  addOrUpdateFormValue,
  createReEvaluation,
  onReevalSentenceUpdated,
];
